// src/components/pages/backtest/BacktestPage.js

import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from 'react-oidc-context'; 
import { ToastContainer, toast } from 'react-toastify';

import BacktestDetailed from "./partials/BacktestDetailed";
import NewPortfolioModal from '../portfolios/modal/NewPortfolioModal'; 

// 💡 Custom Hooks 임포트
import { useBacktestSetup } from './hooks/useBacktestSetup';
import { usePortfolioActions } from './hooks/usePortfolioActions';
import { useBacktestRunner } from './hooks/useBacktestRunner'; 

// 💡 분리된 섹션 컴포넌트 임포트 (새로 생성해야 합니다)
import PortfolioInputSection from './partials/PortfolioInputSection';
import BacktestOptionsSection from './partials/BacktestOptionsSection';
import BenchmarkSection from './partials/BenchmarkSection';
import ActionButtonsSection from './partials/ActionButtonsSection';

import commonStyles from '../../../styles/Common.module.css';
import 'react-toastify/dist/ReactToastify.css';

// =========================================================
// 💡 BacktestPage 컴포넌트: 모든 로직을 훅에 위임하고 렌더링에 집중
// =========================================================
function BacktestPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const auth = useAuth();
    const detailRef = useRef(null);

    // 1. 📋 모든 상태 및 헬퍼 설정
    const setup = useBacktestSetup(location.search, navigate); 
    
    // 2. 💾 포트폴리오 API 통신 및 모달 관리
    const actions = usePortfolioActions(auth, setup, t, navigate);
    
    // 3. ▶️ 실행 로직, 유효성 검사, URL 업데이트
    const runner = useBacktestRunner(setup, detailRef, t, navigate); 

    // 페이지 제목 설정
    useEffect(() => {
        document.title = 'QuanPort-Backtest';
    }, [t]);
    
    // ----------------------------------------------------
    // 🖼️ 렌더링 시작 (컴포넌트 분리로 간결화)
    // ----------------------------------------------------
    return (
        <div className={commonStyles.Container}>
            <div className={commonStyles.introContainer}>
                <p className={commonStyles.introMain}>{t("backtest.backtest")}</p>
                <p className={commonStyles.introSub}>{t("backtest.backtestIntro")}</p>
            </div>

            {/* 2. 티커 및 비중 입력 섹션 (주 포트폴리오) */}
            <PortfolioInputSection setup={setup} actions={actions} runner={runner} auth={auth} t={t} />

            {/* 3. 옵션 (기간) 섹션 */}
            {/* <BacktestOptionsSection setup={setup} t={t} /> */}
            <BacktestOptionsSection setup={setup} t={t} auth={auth} />
            <br />
            
            {/* 4. 벤치마크 섹션  */}
            <BenchmarkSection setup={setup} actions={actions} runner={runner} auth={auth} t={t} />

            {/* 5. 메시지, 실행 및 공유 버튼 */}
            <ActionButtonsSection setup={setup} runner={runner} t={t} />
            
            {/* 6. 백테스트 상세 결과 컴포넌트 */}
            {setup.isBacktestReady && setup.stocksArray.length > 0 && (
                <BacktestDetailed
                    ref={detailRef}
                    stocks={setup.stocksArray} 
                    period={setup.backtestPeriod}
                    rebalancePeriod={setup.rebalancePeriod}
                    optimizedWeights={setup.manualWeights}
                    benchmark={runner.finalBenchmarkParam}
                    startDate={setup.isCustomPeriod ? setup.startDate : ''}
                    endDate={setup.isCustomPeriod ? setup.endDate : ''}
                />
            )}
            
            {/* 7. 포트폴리오 저장 모달 */}
            {actions.showSaveModal && (
                <NewPortfolioModal
                    onClose={() => actions.setShowSaveModal(false)}
                    onCreated={actions.handlePortfolioSaved} 
                    initialHoldings={actions.tempHoldings} 
                    initialName={''} 
                    isForcedPublic={false}
                />
            )}

            <ToastContainer position="top-center" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
        </div>
    );
}

export default BacktestPage;